import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import {
  Sparkles,
  Calendar,
  Wand2,
  MessageSquare,
  HeartHandshake,
  Gift,
  BadgePercent,
  Headset,
  CheckCircle2,
  Printer,
  Smartphone,
  Monitor,
} from "lucide-react";
import { CAMPAIGNS, campaignLandingUrl } from "@/lib/campaigns";

// チラシ URL: /flyer (デフォルト: maruhei) または /flyer/{slug}
function resolveCampaign(slug) {
  const fallback = CAMPAIGNS[0]; // 何も指定がなければ最初のキャンペーン
  if (!slug) return fallback;
  return CAMPAIGNS.find((c) => c.slug === slug) || fallback;
}

/* ===== 共通: ベネフィットカード（表面・大きめ） ===== */
function Benefit({ icon: Icon, badge, title, big, sub, bgGrad, ring, fg }) {
  return (
    <div
      className={`relative rounded-2xl p-3.5 shadow-md ${ring} ${bgGrad} flex flex-col items-center text-center`}
      style={{ minHeight: "160px" }}
    >
      <div
        className={`absolute -top-3 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full text-[10px] font-black tracking-wider ${fg} bg-white shadow ring-1 ring-slate-200`}
      >
        {badge}
      </div>
      <Icon className={`w-7 h-7 ${fg} mt-1.5`} strokeWidth={2.4} />
      <p className={`text-[26px] font-black ${fg} leading-none mt-1.5 tracking-tight`}>{big}</p>
      <p className="text-[13px] font-bold text-slate-900 mt-1 leading-tight">{title}</p>
      <p className="text-[10px] text-slate-600 leading-snug mt-1">{sub}</p>
    </div>
  );
}

/* ===== 共通: スクリーンショットモック ===== */
function DesktopShot({ src, label, tone = "emerald" }) {
  const toneClass = {
    emerald: "bg-emerald-100 text-emerald-700",
    amber: "bg-amber-100 text-amber-700",
    blue: "bg-blue-100 text-blue-700",
    rose: "bg-rose-100 text-rose-700",
    purple: "bg-purple-100 text-purple-700",
    sky: "bg-sky-100 text-sky-700",
  }[tone];
  return (
    <div className="rounded-lg overflow-hidden border border-slate-200 shadow-md bg-slate-50">
      <div className="flex items-center gap-1 px-2 py-1.5 bg-slate-100 border-b border-slate-200">
        <span className="w-1.5 h-1.5 rounded-full bg-rose-300" />
        <span className="w-1.5 h-1.5 rounded-full bg-amber-300" />
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-300" />
        <span className={`ml-1.5 text-[8px] font-bold px-1.5 py-0.5 rounded ${toneClass}`}>
          {label}
        </span>
      </div>
      <img src={src} alt={label} className="w-full block" />
    </div>
  );
}

function PhoneShot({ src, label }) {
  return (
    <div className="flex flex-col items-center">
      <div className="relative rounded-2xl border-[5px] border-slate-800 bg-slate-800 shadow-md overflow-hidden w-[105px]">
        <span className="absolute top-1 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-slate-700 rounded-full z-10" />
        <img src={src} alt={label} className="w-full block" />
      </div>
      <span className="mt-1 text-[8px] font-bold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">
        {label}
      </span>
    </div>
  );
}

/* ===== ページ ===== */
export default function Flyer() {
  const { slug } = useParams();
  const campaign = resolveCampaign(slug);
  const LP_URL = campaignLandingUrl(campaign.slug);
  const webDisplay = `${campaign.slug}.shiftmagic.jp`;
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @page { size: A4; margin: 0; }
      @media print {
        html, body { margin: 0; padding: 0; background: white; }
        .no-print { display: none !important; }
        .flyer-page { box-shadow: none !important; margin: 0 !important; page-break-after: always; }
        .flyer-page:last-child { page-break-after: auto; }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const handlePrint = () => window.print();

  return (
    <div className="min-h-screen bg-slate-200 py-6">
      {/* 操作バー（印刷時は非表示） */}
      <div className="no-print max-w-[210mm] mx-auto mb-4 px-4 flex items-center justify-between">
        <div className="text-sm text-slate-700">
          <p className="font-bold">モニターキャンペーン チラシ（A4両面）</p>
          <p className="text-xs text-slate-500">
            ブラウザの印刷で「両面印刷」を選ぶか、PDFとして保存できます。
          </p>
        </div>
        <button
          onClick={handlePrint}
          data-testid="flyer-print-button"
          className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm px-5 py-2.5 rounded-full shadow-md flex items-center gap-2"
        >
          <Printer className="w-4 h-4" /> 印刷 / PDF 保存
        </button>
      </div>

      {/* ============ 表面 (Page 1) ============ */}
      <div
        data-testid="flyer-front"
        className="flyer-page bg-white shadow-xl mx-auto relative overflow-hidden"
        style={{ width: "210mm", height: "297mm" }}
      >
        {/* 背景デコ */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-emerald-100/60" />
          <div className="absolute top-40 -left-24 w-72 h-72 rounded-full bg-amber-100/50" />
        </div>

        <div className="relative h-full flex flex-col px-9 py-6">
          {/* ヘッダ */}
          <header className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <img
                src="/yasamaru-celebrate-128.png"
                alt="やさまる"
                className="w-11 h-11"
              />
              <div className="leading-tight">
                <p className="text-[9px] font-bold text-emerald-700 tracking-widest">
                  YASAMARU SHIFT
                </p>
                <p className="text-lg font-bold text-slate-900">やさまるシフト</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-bold text-slate-500">Web: {webDisplay}</p>
            </div>
          </header>

          {/* ✨ 巨大なキャンペーンバナー（リボン風） ✨ */}
          <div className="mt-3 relative">
            <div
              className="rounded-2xl px-5 py-3 shadow-lg relative overflow-hidden"
              style={{
                background:
                  "linear-gradient(135deg, #DC2626 0%, #F97316 60%, #F59E0B 100%)",
              }}
            >
              {/* キラキラ装飾 */}
              <Sparkles className="absolute top-2 right-3 w-5 h-5 text-yellow-200/70" />
              <Sparkles className="absolute bottom-2 left-3 w-4 h-4 text-yellow-200/50" />
              <div className="flex items-center justify-between text-white">
                <div>
                  <div className="inline-block bg-white text-rose-600 text-[10px] font-black px-2 py-0.5 rounded-md tracking-widest shadow-sm">
                    モニター企業 大募集
                  </div>
                  <p className="mt-1 text-[26px] font-black leading-none tracking-tight">
                    先着 <span className="text-yellow-300 text-[36px]">30社</span> 限定
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] font-bold text-yellow-100 tracking-wider">
                    応 募 締 切
                  </p>
                  <p className="text-2xl font-black tracking-tight leading-tight">
                    2026.<span className="text-yellow-200">6/5</span>
                  </p>
                  <p className="text-[10px] font-bold text-yellow-100 mt-0.5">まで</p>
                </div>
              </div>
            </div>
          </div>

          {/* メインキャッチ */}
          <div className="mt-4 text-center">
            <h1 className="text-[34px] font-black text-slate-900 leading-[1.1] tracking-tight">
              シフト作成の
              <span className="text-emerald-600">「めんどう」</span>
              を、<br />
              やさしく、まるく。
            </h1>
            <p className="mt-1.5 text-[11px] text-slate-600 leading-relaxed">
              希望を集める・自動配置する・連絡する。店長の負担を減らす、小さな店舗のための<b>シフト管理SaaS</b>。
            </p>
          </div>

          {/* 実画面プレビュー */}
          <div className="mt-3 relative">
            <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
              <div className="relative">
                <DesktopShot src="/lp-screen-3-adjust.jpg" label="管理者画面 / 日を調整" tone="emerald" />
                <div className="absolute -top-2 -left-2 bg-emerald-500 text-white text-[9px] font-bold px-2 py-1 rounded-lg shadow-md rotate-[-3deg]">
                  AIが自動配置 →
                </div>
              </div>
              <PhoneShot src="/lp-screen-5-mobile.jpg" label="スタッフ画面" />
            </div>
            <p className="text-center text-[10px] text-slate-500 mt-1 font-bold">
              ↑ 実際のアプリ画面です（PC・スマホ両対応）
            </p>
          </div>

          {/* 3大特典 */}
          <div className="mt-5">
            <p className="text-center text-[11px] font-black tracking-widest text-slate-700 mb-3">
              ━━━━ モニター企業 限定 3大特典 ━━━━
            </p>
            <div className="grid grid-cols-3 gap-3 px-1">
              <Benefit
                icon={Gift}
                badge="特典 1"
                big="2ヶ月"
                title="無料お試し"
                sub="登録から最初の2ヶ月は完全無料"
                bgGrad="bg-gradient-to-br from-emerald-50 to-emerald-100/80"
                ring="ring-2 ring-emerald-300"
                fg="text-emerald-700"
              />
              <Benefit
                icon={BadgePercent}
                badge="特典 2"
                big="50% OFF"
                title="さらに 2ヶ月"
                sub="3〜4ヶ月目は通常料金の半額"
                bgGrad="bg-gradient-to-br from-amber-50 to-amber-100/80"
                ring="ring-2 ring-amber-300"
                fg="text-amber-700"
              />
              <Benefit
                icon={Headset}
                badge="特典 3"
                big="無料"
                title="初期設定サポート"
                sub="導入時の設定をスタッフが伴走"
                bgGrad="bg-gradient-to-br from-blue-50 to-blue-100/80"
                ring="ring-2 ring-blue-300"
                fg="text-blue-700"
              />
            </div>
          </div>

          {/* モニター条件 */}
          <div className="mt-3 rounded-xl border border-dashed border-emerald-400 bg-emerald-50/40 px-4 py-2">
            <p className="text-center text-[10px] font-bold text-emerald-800 tracking-wide">
              モニター条件
            </p>
            <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5 text-[10px] text-slate-800">
              <div className="flex items-start gap-1.5">
                <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0 mt-0.5" />
                <span>導入後、簡単な感想アンケートにご協力</span>
              </div>
              <div className="flex items-start gap-1.5">
                <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0 mt-0.5" />
                <span>事例として店名・業種の掲載を許諾</span>
              </div>
              <div className="flex items-start gap-1.5">
                <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0 mt-0.5" />
                <span>店長/担当者がメッセージで連絡可能</span>
              </div>
              <div className="flex items-start gap-1.5">
                <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0 mt-0.5" />
                <span>常時 3 名以上のスタッフがいる店舗</span>
              </div>
            </div>
          </div>

          {/* CTA + QR */}
          <div className="mt-auto flex items-end justify-between gap-5">
            <div className="flex-1">
              <p className="text-[10px] font-bold text-slate-500 tracking-widest mb-1">
                お申込みはこちら
              </p>
              <p className="text-xl font-black text-slate-900 leading-tight">
                右の QR を読み取って<br />
                <span className="text-emerald-600">2分で無料トライアル申込</span>
              </p>
              <p className="text-[10px] text-slate-500 mt-1.5">
                Web：<b className="text-slate-900">{webDisplay}</b>
              </p>
              <p className="text-[9px] text-slate-400">
                ※ フォームに「モニター希望」とご記入ください
              </p>
            </div>
            <div className="bg-white border-4 border-emerald-500 rounded-xl p-2 shadow-md">
              <QRCodeSVG value={LP_URL} size={105} level="M" />
              <p className="text-[8px] text-center text-slate-500 mt-0.5 font-bold">
                スマホで読取
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ============ 裏面 (Page 2) ============ */}
      <div
        data-testid="flyer-back"
        className="flyer-page bg-white shadow-xl mx-auto mt-8 relative overflow-hidden"
        style={{ width: "210mm", height: "297mm" }}
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-full h-2 bg-emerald-500" />
        </div>

        <div className="relative h-full flex flex-col px-9 py-7">
          {/* ヘッダ */}
          <header className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-600" />
              実際の画面でご紹介します
            </h2>
            <p className="text-[10px] text-slate-400 font-bold tracking-widest">
              SHIFT MANAGEMENT MADE EASY
            </p>
          </header>

          {/* 機能 1: 配置マジック + 5ステップ画面 */}
          <section className="grid grid-cols-[160px_1fr] gap-3 items-center">
            <div>
              <DesktopShot src="/lp-screen-1-prep.jpg" label="① 今月の準備" tone="amber" />
            </div>
            <div className="px-1">
              <div className="flex items-center gap-1.5 mb-1">
                <Wand2 className="w-4 h-4 text-emerald-600" />
                <p className="font-bold text-[13px] text-slate-900">配置マジック × 5ステップ</p>
              </div>
              <p className="text-[11px] text-slate-600 leading-relaxed">
                <b>「今月の準備 → 配置マジック → 日を調整 → スタッフ状況 → 確定」</b> の5ステップで月のシフトが完成。
                希望と必要人数を AI が見て、<b>5秒で月のベース配置を自動生成</b>。
                「希望優先」「必要人数厳守」モードを切替可能。
              </p>
            </div>
          </section>

          {/* 機能 2: 検討依頼 */}
          <section className="mt-3 grid grid-cols-[1fr_160px] gap-3 items-center">
            <div className="px-1">
              <div className="flex items-center gap-1.5 mb-1">
                <MessageSquare className="w-4 h-4 text-amber-600" />
                <p className="font-bold text-[13px] text-slate-900">検討依頼（打診型お願い）</p>
              </div>
              <p className="text-[11px] text-slate-600 leading-relaxed">
                候補スタッフに「もしかしたらお願いするかも」を気軽に打診。<b>承諾／NG／代替時間</b>がバッジで一目瞭然。
                承諾されてもシフトには自動追加されず、店長が最終判断する<b>「意思のある運用」</b>を実現します。
              </p>
            </div>
            <div>
              <DesktopShot src="/lp-screen-3-adjust.jpg" label="日を調整 / 候補表示" tone="amber" />
            </div>
          </section>

          {/* 機能 3: 代理入力AI */}
          <section className="mt-3 grid grid-cols-[160px_1fr] gap-3 items-center">
            <div>
              <DesktopShot src="/lp-screen-6-proxy-ai-result.jpg" label="代理入力 AI" tone="purple" />
            </div>
            <div className="px-1">
              <div className="flex items-center gap-1.5 mb-1">
                <Sparkles className="w-4 h-4 text-purple-600" />
                <p className="font-bold text-[13px] text-slate-900">代理入力 × AI</p>
              </div>
              <p className="text-[11px] text-slate-600 leading-relaxed">
                <b>「土日休み、平日は時間あれば入ります」</b> のような LINE 文面をそのまま貼付け。
                AI が日付ごとの <b>◎ / △ / ×</b> に変換してくれます。
                提出が遅いスタッフの代行入力に最適。
              </p>
            </div>
          </section>

          {/* 機能 4: スタッフ画面 */}
          <section className="mt-3 grid grid-cols-[1fr_auto] gap-3 items-center">
            <div className="px-1">
              <div className="flex items-center gap-1.5 mb-1">
                <Smartphone className="w-4 h-4 text-rose-600" />
                <p className="font-bold text-[13px] text-slate-900">スタッフはスマホで完結</p>
              </div>
              <p className="text-[11px] text-slate-600 leading-relaxed">
                <b>希望提出はタップだけ</b>。◎入れます / △必要なら / ×入れません を選んで時間を入力。
                確定シフトも自分のスマホでいつでも確認できます。
                <b>店長への変更依頼</b>もアプリ内のメッセージで完結。
              </p>
            </div>
            <div className="flex gap-2">
              <PhoneShot src="/lp-screen-5-request.jpg" label="希望提出" />
              <PhoneShot src="/lp-screen-5-mobile.jpg" label="マイシフト" />
            </div>
          </section>

          {/* お申込みの流れ */}
          <section className="mt-4 rounded-xl bg-slate-50 border border-slate-200 px-4 py-3">
            <h3 className="text-[13px] font-bold text-slate-900 mb-2 flex items-center gap-1.5">
              <HeartHandshake className="w-4 h-4 text-emerald-600" />
              お申込みから利用開始までの流れ
            </h3>
            <div className="grid grid-cols-4 gap-3 text-center">
              <div>
                <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 font-bold text-xs flex items-center justify-center mx-auto">1</div>
                <p className="text-[10px] font-bold text-slate-800 mt-1">QR から申込み</p>
                <p className="text-[9px] text-slate-500 leading-tight mt-0.5">「モニター希望」記入</p>
              </div>
              <div>
                <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 font-bold text-xs flex items-center justify-center mx-auto">2</div>
                <p className="text-[10px] font-bold text-slate-800 mt-1">運営から連絡</p>
                <p className="text-[9px] text-slate-500 leading-tight mt-0.5">条件のご案内</p>
              </div>
              <div>
                <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 font-bold text-xs flex items-center justify-center mx-auto">3</div>
                <p className="text-[10px] font-bold text-slate-800 mt-1">ID/PW を発行</p>
                <p className="text-[9px] text-slate-500 leading-tight mt-0.5">店舗別URL付き</p>
              </div>
              <div>
                <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 font-bold text-xs flex items-center justify-center mx-auto">4</div>
                <p className="text-[10px] font-bold text-slate-800 mt-1">無料サポート</p>
                <p className="text-[9px] text-slate-500 leading-tight mt-0.5">初期設定を伴走</p>
              </div>
            </div>
          </section>

          {/* フッタ：再掲CTA */}
          <div className="mt-auto pt-4 border-t-2 border-dashed border-emerald-300 flex items-center justify-between gap-5">
            <div className="flex-1">
              <p className="text-[10px] font-bold text-rose-700 tracking-widest">
                応募締切：2026年6月5日 / 先着30社
              </p>
              <p className="text-lg font-black text-slate-900 mt-1 leading-tight">
                今ならお得に始められます。<br />
                <span className="text-emerald-600">右のQRで2分で無料トライアル申込。</span>
              </p>
              <p className="text-[11px] text-slate-600 mt-1.5">
                Web: <b>{webDisplay}</b>
              </p>
            </div>
            <div className="bg-white border-4 border-emerald-500 rounded-xl p-1.5 shadow-md">
              <QRCodeSVG value={LP_URL} size={90} level="M" />
            </div>
          </div>
        </div>
      </div>

      {/* フッタ操作バー（印刷時非表示） */}
      <div className="no-print mt-6 text-center text-xs text-slate-500">
        Ctrl/Cmd + P で印刷ダイアログを開きます。プレビューで「両面印刷」を選択してください。
      </div>
    </div>
  );
}
